/**
 * Carry a post-signup destination across the mandatory onboarding step.
 *
 * A visitor who clicks "Sign up to message" on a search result is sent to
 * /register?next=/find-partners?sport=tennis. Registration always routes to
 * /onboarding, so the intended destination has to be remembered across a
 * navigation that would otherwise lose it - otherwise a recruit who searched
 * by sport and city lands on an unfiltered partner list and has to redo the
 * search that brought them in.
 *
 * sessionStorage rather than a router state or a query param because the hop
 * through /onboarding involves a full profile write, and because a stale
 * "next" must not survive the tab being closed. Reading it clears it, so
 * finishing onboarding twice cannot bounce the user onward again.
 */

const KEY = "post_onboarding_redirect";

/**
 * Only same-origin absolute paths are allowed.
 *
 * A value like "//evil.example.com" is protocol-relative, so `startsWith("/")`
 * alone would accept it and turn registration into an open redirect - the user
 * would supply a genuine email and password, then be handed to a lookalike site
 * immediately afterwards, which is exactly the shape of a credential-phishing
 * flow. Requiring a leading "/" while rejecting "//" closes that.
 */
export function sanitizeRedirect(next) {
  if (typeof next !== "string") return null;
  if (!next.startsWith("/")) return null;
  if (next.startsWith("//")) return null;
  if (next.startsWith("/\\")) return null;
  return next;
}

export function stashPostOnboardingRedirect(next) {
  const safe = sanitizeRedirect(next);
  if (!safe) return;
  try {
    sessionStorage.setItem(KEY, safe);
  } catch {
    // Private browsing can refuse storage. The default post-onboarding
    // destination still works, so failing here is not worth surfacing.
  }
}

/** Returns the stashed destination once, then forgets it. Null if there is none. */
export function consumePostOnboardingRedirect() {
  try {
    const stored = sessionStorage.getItem(KEY);
    if (!stored) return null;
    sessionStorage.removeItem(KEY);
    return sanitizeRedirect(stored);
  } catch {
    return null;
  }
}
